/**
 * Formatting, in the conventions an Indian CA firm actually uses.
 *
 * docs/ui-design.md §8: Indian number format — ₹13,51,000, lakh grouping, not thousands.
 * Intl's 'en-IN' locale does the grouping correctly, so this is a thin layer
 * over it rather than hand-rolled digit splicing.
 */

const INR = new Intl.NumberFormat('en-IN', {
	style: 'currency',
	currency: 'INR',
	maximumFractionDigits: 0,
});

const INR_PRECISE = new Intl.NumberFormat('en-IN', {
	style: 'currency',
	currency: 'INR',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const PLAIN = new Intl.NumberFormat('en-IN');

const PLAIN_PRECISE = new Intl.NumberFormat('en-IN', {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

function asNumber(value: unknown): number | null {
	if (value === null || value === undefined || value === '') return null;
	const n =
		typeof value === 'number'
			? value
			: Number(String(value).replace(/,/g, ''));
	return Number.isFinite(n) ? n : null;
}

/** ₹13,51,000 — for a figure in a table or a stat tile. */
export function money(value: unknown, precise = false): string {
	const n = asNumber(value);
	if (n === null) return '—';
	return precise ? INR_PRECISE.format(n) : INR.format(n);
}

/**
 * ₹13.51 L — the form a partner reads a headline in.
 *
 * Crore above a crore, lakh below it. Anything under a lakh is shown in full,
 * because "₹0.42 L" is harder to read than "₹42,000".
 */
export function moneyShort(value: unknown): string {
	const n = asNumber(value);
	if (n === null) return '—';
	const abs = Math.abs(n);
	const sign = n < 0 ? '-' : '';
	if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)} Cr`;
	if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)} L`;
	return INR.format(n);
}

export function count(value: unknown): string {
	const n = asNumber(value);
	return n === null ? '—' : PLAIN.format(n);
}

export function decimal(value: unknown, places = 2): string {
	const n = asNumber(value);
	if (n === null) return '—';
	return places === 2
		? PLAIN_PRECISE.format(n)
		: new Intl.NumberFormat('en-IN', {
				minimumFractionDigits: places,
				maximumFractionDigits: places,
			}).format(n);
}

/** A quantity or rate at the precision the data carries (up to 4 places),
 *  grouped, trailing zeros trimmed — 1,250 · 12.5 · 1.125. (08 §4) */
export function quantity(value: unknown): string {
	const n = asNumber(value);
	return n === null ? '—' : QUANTITY.format(n);
}
const QUANTITY = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 4 });

export function percent(value: unknown, places = 1): string {
	const n = asNumber(value);
	return n === null ? '—' : `${n.toFixed(places)}%`;
}

/** 07 Apr 2026 — unambiguous, and never the US month-first order. */
export function date(value: unknown): string {
	if (!value) return '—';
	// A plain date from Postgres arrives as 'YYYY-MM-DD'. Parsing it with the
	// Date constructor would apply UTC and shift it a day in some timezones, so
	// split it by hand.
	const text = String(value);
	const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
	if (plain) {
		const [, year, month, day] = plain;
		const parsed = new Date(Number(year), Number(month) - 1, Number(day));
		return parsed.toLocaleDateString('en-IN', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
		});
	}
	const parsed = new Date(text);
	if (Number.isNaN(parsed.getTime())) return text;
	return parsed.toLocaleDateString('en-IN', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	});
}

/** 07 Apr 2026, 14:32 — for an audit trail, where the time matters. */
export function dateTime(value: unknown): string {
	if (!value) return '—';
	const parsed = new Date(String(value));
	if (Number.isNaN(parsed.getTime())) return String(value);
	return `${date(value)}, ${parsed.toLocaleTimeString('en-IN', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})}`;
}

/** "3 minutes ago" — for activity feeds, where precision is noise. */
export function ago(value: unknown): string {
	if (!value) return '';
	const then = new Date(String(value)).getTime();
	if (Number.isNaN(then)) return '';
	const seconds = Math.round((Date.now() - then) / 1000);
	if (seconds < 60) return 'just now';
	const minutes = Math.round(seconds / 60);
	if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
	const hours = Math.round(minutes / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
	const days = Math.round(hours / 24);
	if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
	return date(value);
}

export function bytes(value: unknown): string {
	const n = asNumber(value);
	if (n === null) return '—';
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
	if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
	return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** 'NEEDS_MAPPING' → 'Needs mapping', as a last resort when no label exists. */
export function humanise(value: unknown): string {
	if (!value) return '';
	const text = String(value).replace(/_/g, ' ').toLowerCase();
	return text.charAt(0).toUpperCase() + text.slice(1);
}

export function plural(n: number, one: string, many?: string): string {
	return n === 1 ? one : (many ?? `${one}s`);
}

/** 2,772,481 -> "2.77M". For a row count in a table cell, where the full
 *  number costs more width than the extra digits are worth. */
export function compactRows(rows: number): string {
	if (rows < 1_000) return String(rows);
	if (rows < 1_000_000)
		return `${(rows / 1_000).toFixed(rows < 10_000 ? 1 : 0)}k`;
	return `${(rows / 1_000_000).toFixed(2)}M`;
}

/** 95000 -> "1m 35s". A run's elapsed time. */
export function durationLabel(ms: number | null | undefined): string {
	if (ms === null || ms === undefined) return '—';
	const seconds = Math.round(ms / 1000);
	if (seconds < 60) return `${seconds}s`;
	return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}
